import { useContext, useState } from 'react';
import dayjs from 'dayjs';
import { X } from 'react-feather';

import { useLanguage } from 'assets/languages';
import { LOCALES } from 'constants/locales';
import { PAYMENT_PRODUCT_TYPE } from 'constants/payment';
import BRAND from 'constants/brand';
import { debounce } from 'helpers/debounce';
import Button from 'components/button';
import { CampaignFormContext } from 'pages/campaigns/campaignForm/campaignForm.context';
import CampaignFormStepButtons from '../../../../../campaignFormStepButtons/campaignFormStepButtons';
import { CampaignFormStepThreeContext } from '../../campaignFormStepThree.context';
import ManagerFieldGroups from '../managerFieldGroups/managerFieldGroups';
import GuidelineCheckModal from '../beautyCampaignFormStepThree/guidelineCheckModal';
import GuidelineWebViewModal from '../beautyCampaignFormStepThree/guidelineWebViewModal';

const FashionCampaignFormStepThree = () => {
  const language = useLanguage();
  const languageTexts = { ...language.common, ...language.pages.campaigns };
  const { campaignFormMethods, modal, brand, settings, temporaryKey, products, setShowLoader } = useContext(CampaignFormContext);
  const { seedingPassEndsAt, isSeedingPassActive, isFormValid, createCampaign, proceedPayment, onClickNextStep } =
    useContext(CampaignFormStepThreeContext);

  const [showGuidelineModal, setShowGuidelineModal] = useState<boolean>(false);
  const [showWebViewModal, setShowWebViewModal] = useState<boolean>(false);

  const handleGuidelineConfirm = () => {
    setShowGuidelineModal(false);
    setShowWebViewModal(true);
  };

  const handleWebViewClose = () => {
    setShowWebViewModal(false);
  };

  const handleWebViewConfirm = () => {
    setShowWebViewModal(false);

    // 가이드라인 확인 후 기존 결제 로직 진행
    const isKyolookSeedingPassActive = BRAND.isKyolookSeedingPassActive(brand);
    if (
      isKyolookSeedingPassActive ||
      (brand.hasSeedingPass && brand.isSeedingPassAutoPurchaseEnabled) ||
      (brand.hasSeedingPass && isSeedingPassActive) ||
      brand.priceSeedingPass === 0
    ) {
      createCampaign();
    } else {
      showPaymentModal();
    }
  };

  const showPaymentModal = () => {
    debounce(() => {
      modal.showOverlays({
        className: 'w-full md:min-w-[640px] justify-center',
        content: () => {
          return (
            <div className="p-10 bg-white rounded-2xl">
              <div className="flex flex-col">
                <div className="flex flex-col">
                  <div className="flex flex-row w-full justify-between mb-6">
                    <div className="flex flex-col gap-4">
                      <span className="text-lg font-semibold md:text-2xl md:font-bold text-center md:text-start">
                        {languageTexts.pay}
                      </span>

                      {!isSeedingPassActive && seedingPassEndsAt ? (
                        <span className="text-base tracking-tighter">
                          {languageTexts._createPaymentText1(
                            seedingPassEndsAt.format(
                              languageTexts.date_format,
                            ),
                          )}
                        </span>
                      ) : (
                        <span className="font-bold text-lg tracking-tighter">
                          {languageTexts._createPaymentText2(
                            settings.durationSeedingPass,
                            brand.priceSeedingPass,
                            settings.currencyText,
                          )}
                        </span>
                      )}
                    </div>
                    <div onClick={modal.closeOverlays}>
                      <X />
                    </div>
                  </div>

                  <div className="flex flex-col my-3">
                    <div className="w-full flex flex-col">
                      <Button
                        className="px-3 py-2 text-center rounded-lg"
                        buttonColor="#BF664A"
                        text={languageTexts._createPaymentUnlimitedPrice(
                          settings.durationSeedingPass,
                          brand.priceSeedingPass,
                          settings.currencyText,
                        )}
                        justify="center"
                        textColor="white"
                        borderColor="transparent"
                        textClassName="font-bold text-xl tracking-tighter text-white"
                      />
                    </div>

                    <div className="flex flex-col justify-center mx-auto w-full bg-[#FFF7F7]">
                      <span className="font-bold text-xl text-center tracking-tighter py-4 mt-[12px]">
                        {languageTexts._create_payment_text21(
                          settings.currencyText,
                        )}
                      </span>

                      {brand.isSeedingPassWelcomeBonusEnabled === 1 &&
                        brand.hasExperiencedSeedingPass === 0 && (
                          <span className="font-bold text-xl text-center tracking-tighter">
                            {languageTexts.create_payment_text22}
                          </span>
                        )}

                      <div className="opacity-50 text-black text-sm font-normal text-center mx-auto pb-7">
                        {languageTexts._create_payment_text23(
                          settings.durationSeedingPass,
                        )}
                        <br />
                        {languageTexts.create_payment_text24}
                      </div>
                    </div>
                  </div>

                  <div className="flex flex-col gap-4">
                    <Button
                      text={languageTexts.create_payment_button_1}
                      buttonColor="black"
                      textColor="white"
                      justify="center"
                      onClick={async () => {
                        proceedPayment(PAYMENT_PRODUCT_TYPE.SEEDING_PASS);
                      }}
                    />

                    {/* @note 250409 패션 건당 결제 비활성화 */}
                    {process.env.LOCALE == LOCALES.KO &&
                      !brand.isCategoryFashion && (
                        <div className="flex flex-col gap-3">
                          <Button
                            text={languageTexts.create_payment_button_2}
                            buttonColor="white"
                            borderColor="black"
                            textColor="black"
                            justify="center"
                            bordered
                            onClick={async () => {
                              proceedPayment(
                                PAYMENT_PRODUCT_TYPE.SEEDING,
                              );
                            }}
                          />

                          <div className="flex flex-col items-center">
                            <span className="text-sm tracking-tighter opacity-50">
                              {languageTexts._create_payment_text31(
                                settings.durationSeedingPass,
                              )}
                            </span>

                            <span className="text-sm tracking-tighter opacity-50">
                              {languageTexts.create_payment_text32}
                            </span>
                          </div>
                        </div>
                      )}
                  </div>
                </div>
              </div>
            </div>
          );
        },
      });
    }, 400);
  };

  const onClickNext = () => {
    onClickNextStep({
      validateCampaign: () => {
        // @note 최소한의 방어코드들. 시간날 때 생성 조건 검증용 API를 만들어서 에러 메세지까지 모달로 띄우기
        const recruitStartsAt = campaignFormMethods.getValues('recruitStartsAt');
        if (dayjs(recruitStartsAt).isBefore(dayjs(), 'day')) {
          return false;
        }
        return true;
      },
      onSuccessStoreOrUpdateTemporaryCampaignCallback: () => {
        // 가이드라인 최종 체크 모달 띄우기
        setShowGuidelineModal(true);
      },
    });
  };

  return (
    <>
      {/* 브랜드 담당자 및 반품 관련 정보 */}
      <ManagerFieldGroups />

      <CampaignFormStepButtons
        disabledNextButton={!isFormValid}
        onClickNext={onClickNext}
      />

      {/* 가이드라인 최종 체크 모달 */}
      {showGuidelineModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div
            className="absolute inset-0 bg-black bg-opacity-50"
            onClick={(e) => e.stopPropagation()}
          />
          <div className="relative z-10">
            <GuidelineCheckModal onConfirm={handleGuidelineConfirm} />
          </div>
        </div>
      )}

      {/* 가이드라인 WebView 모달 */}
      {showWebViewModal && temporaryKey && campaignFormMethods && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div
            className="absolute inset-0 bg-black bg-opacity-50"
            onClick={handleWebViewClose}
          />
          <div className="relative z-10">
            {(() => {
              const productID = products?.[0]?.id;

              if (!productID) {
                return null;
              }

              return (
                <GuidelineWebViewModal
                  productId={productID}
                  temporaryKey={temporaryKey}
                  onClose={handleWebViewClose}
                  onConfirm={handleWebViewConfirm}
                />
              );
            })()}
          </div>
        </div>
      )}
    </>
  );
};

export default FashionCampaignFormStepThree;
